import { AnimatedPageHeader } from '@components/index'
import { Table } from '@components/table'
import { PageRead } from '@components/table/types'
import { useClients } from '@services/users/queries/useClients'
import type { SortOrder } from '@components/table/types'
import { Check, MessageSquare, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { CompletitionIndex, UserList } from 'types/api'

import { cn } from '@/lib/utils'

import { UsersTableFilters } from './UsersTableFilters'

const SORT_DIRECTIONS: SortOrder[] = ['ascend', 'descend']

const ROLE_TONE: Record<string, string> = {
  ADMIN: 'bg-destructive/10 text-destructive',
  USER: 'bg-info-soft text-info',
  ROOMMATE: 'bg-success-soft text-success',
  LANDLORD: 'bg-peach/25 text-warning'
}

function YesNo({ value }: { value: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
        value ? 'bg-primary/15 text-primary' : 'bg-ink/[0.07] text-ink/60'
      )}
    >
      {value ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
    </span>
  )
}

function pct(index?: CompletitionIndex, key?: keyof CompletitionIndex) {
  if (!index || key == null) return '—'
  const value = index[key]
  return typeof value === 'number' ? `${Math.floor(value)} %` : '—'
}

export function UsersTable() {
  const { t } = useTranslation('users')

  const columns = [
    {
      title: t('form.labels.unreadMessages'),
      dataIndex: 'unread_messages',
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      align: 'center' as const,
      render: (value: number) => (
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold',
            value > 0
              ? 'bg-peach/30 text-warning'
              : 'bg-ink/[0.05] text-ink/45'
          )}
        >
          <MessageSquare className="h-3.5 w-3.5" />
          {value ?? 0}
        </span>
      )
    },
    {
      title: t('completition_index.profile_index'),
      dataIndex: 'completition_index',
      render: (index: CompletitionIndex) => (
        <span className="text-sm font-medium text-ink">
          {pct(index, 'profile_completition')}
        </span>
      )
    },
    {
      title: t('completition_index.application_index'),
      dataIndex: 'completition_index',
      render: (index: CompletitionIndex) => (
        <span className="text-sm font-medium text-ink">
          {pct(index, 'application_completition')}
        </span>
      )
    },
    {
      title: t('form.labels.wasContacted'),
      dataIndex: 'was_contacted',
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      align: 'center' as const,
      render: (value: boolean) => <YesNo value={value} />
    },
    {
      title: t('form.labels.name'),
      dataIndex: 'first_name',
      render: (_: string, record: UserList) => {
        const name = [record.first_name, record.last_name]
          .filter(Boolean)
          .join(' ')
        return <span className="font-medium text-ink">{name || '-'}</span>
      },
      sorter: true,
      sortDirections: SORT_DIRECTIONS
    },
    {
      title: t('form.labels.email'),
      dataIndex: 'email',
      sorter: true,
      sortDirections: SORT_DIRECTIONS
    },
    {
      title: t('form.labels.phone'),
      dataIndex: 'phone',
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      render: (value: string) => value || '-'
    },
    {
      title: t('form.labels.role'),
      dataIndex: 'role',
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      render: (role: string, record: UserList) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
              ROLE_TONE[role] ?? 'bg-ink/[0.07] text-ink/60'
            )}
          >
            {t(`filters.roles.${role?.toLowerCase()}`, { defaultValue: role })}
          </span>
          {record.is_partner && (
            <span className="inline-flex items-center rounded-full bg-purple-soft px-2.5 py-0.5 text-xs font-semibold text-purple">
              Partner
            </span>
          )}
        </div>
      )
    },
    {
      title: t('form.labels.dateJoined'),
      dataIndex: 'date_joined',
      render: (value: string) =>
        value ? new Date(value).toLocaleDateString('sk-SK') : '-',
      sorter: true,
      sortDirections: SORT_DIRECTIONS
    }
  ]

  const query = useClients()

  return (
    <div className="space-y-6">
      <AnimatedPageHeader title={t('users')} />
      <UsersTableFilters />
      <Table<PageRead<UserList>>
        query={query}
        columns={columns}
        id="users"
        hasSearch={false}
      />
    </div>
  )
}
